import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ImagePlus, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/http";
import { resolveSrc } from "@/content/ContentContext";
import { blankFrom } from "@/content/schemas";

const arr = (v) => (Array.isArray(v) ? v : []);
const Nested = {};
const move = (a, i, d) => {
  const j = i + d;
  if (j < 0 || j >= a.length) return a;
  const b = [...a];
  [b[i], b[j]] = [b[j], b[i]];
  return b;
};

const Label = ({ children }) => <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-[#D9CFC2]">{children}</span>;
const inputCls = "w-full rounded-xl border border-[#F7F4EE]/14 bg-[#F7F4EE]/5 px-3 py-2.5 text-sm text-[#F7F4EE] outline-none transition-colors focus:border-[#E69C24]";
const MiniBtn = ({ onClick, label, children, danger, testid }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`flex h-8 w-8 items-center justify-center rounded-full border border-[#F7F4EE]/14 text-[#F7F4EE] transition-colors ${danger ? "hover:bg-[#C85A32]" : "hover:bg-[#F7F4EE]/10"}`}>
    {children}
  </button>
);

const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image envoyée");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-[#F7F4EE]/14 bg-[#F7F4EE]/5">
        {value ? <img src={resolveSrc(value)} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <button type="button" onClick={() => ref.current?.click()} className="chip" data-testid={`${testid}-upload`} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{value ? "Remplacer l'image" : "Envoyer une image"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…ou collez un lien d'image" />
      </div>
    </div>
  );
};

const SpotifyField = ({ item, setItem, testid }) => {
  const [q, setQ] = useState(item?.album ? `${item.artist || ""} ${item.album}`.trim() : "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await http.get("/admin/spotify/search", { params: { q } });
      setRes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder="Nom d'album ou d'artiste" data-testid={`${testid}-query`} />
        <button type="button" onClick={search} className="chip shrink-0" data-testid={`${testid}-search`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Chercher
        </button>
      </div>
      {item?.spotify && <p className="truncate text-xs text-[#8FC29B]">Lié à Spotify : {item.spotify}</p>}
      {res && (
        <div className="grid gap-1.5" data-testid={`${testid}-results`}>
          {res.length === 0 && <p className="text-xs text-[#C5BDB3]">Aucun résultat.</p>}
          {res.map((r, i) => (
            <button key={r.spotify} type="button" data-testid={`${testid}-result-${i}`}
              onClick={() => { setItem({ ...item, album: r.album, artist: r.artist, year: r.year, cover: r.cover, spotify: r.spotify }); setRes(null); }}
              className="flex items-center gap-3 rounded-xl p-1.5 text-left transition-colors hover:bg-[#F7F4EE]/8">
              {r.cover && <img src={r.cover} alt="" className="h-10 w-10 rounded-md object-cover" />}
              <span className="min-w-0"><span className="block truncate text-sm text-[#F7F4EE]">{r.album}</span><span className="block truncate text-xs text-[#C5BDB3]">{r.artist} · {r.year}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StringsField = ({ value, onChange, multiline, testid }) => {
  const items = arr(value);
  const set = (i, v) => onChange(items.map((x, k) => (k === i ? v : x)));
  return (
    <div className="space-y-2">
      {items.map((s, i) => (
        <div key={i} className="flex items-start gap-2">
          {multiline ? <textarea rows={3} className={inputCls} value={s} onChange={(e) => set(i, e.target.value)} />
            : <input className={inputCls} value={s} onChange={(e) => set(i, e.target.value)} />}
          <MiniBtn label="Monter" onClick={() => onChange(move(items, i, -1))}><ArrowUp className="h-3.5 w-3.5" /></MiniBtn>
          <MiniBtn label="Supprimer" danger onClick={() => onChange(items.filter((_, k) => k !== i))}><Trash2 className="h-3.5 w-3.5" /></MiniBtn>
        </div>
      ))}
      <button type="button" className="chip" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}><Plus className="h-4 w-4" />Ajouter</button>
    </div>
  );
};

const ListEditor = ({ field, value, onChange, testid }) => {
  const items = arr(value);
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-[#F7F4EE]/10 bg-[#F7F4EE]/[0.03]">
          <div className="flex items-center gap-2 p-2 pl-4">
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setOpen(open === i ? null : i)} data-testid={`${testid}-item-${i}`}>
              <ChevronDown className={`h-4 w-4 shrink-0 text-[#E69C24] transition-transform ${open === i ? "rotate-180" : ""}`} />
              <span className="truncate text-sm text-[#F7F4EE]">{it?.[field.itemLabel] || `Élément ${i + 1}`}</span>
            </button>
            <MiniBtn label="Monter" onClick={() => onChange(move(items, i, -1))}><ArrowUp className="h-3.5 w-3.5" /></MiniBtn>
            <MiniBtn label="Descendre" onClick={() => onChange(move(items, i, 1))}><ArrowDown className="h-3.5 w-3.5" /></MiniBtn>
            <MiniBtn label="Supprimer" danger testid={`${testid}-delete-${i}`} onClick={() => { if (window.confirm("Supprimer cet élément ?")) onChange(items.filter((_, k) => k !== i)); }}><Trash2 className="h-3.5 w-3.5" /></MiniBtn>
          </div>
          {open === i && (
            <div className="space-y-4 border-t border-[#F7F4EE]/10 p-4">
              <Nested.Obj fields={field.fields} value={it} onChange={(v) => onChange(items.map((x, k) => (k === i ? v : x)))} testid={`${testid}-${i}`} />
            </div>
          )}
        </div>
      ))}
      <button type="button" className="chip" data-testid={`${testid}-add`} onClick={() => { onChange([...items, blankFrom(field.fields)]); setOpen(items.length); }}>
        <Plus className="h-4 w-4" />Ajouter
      </button>
    </div>
  );
};

const FieldEditor = ({ field, value, onChange, item, setItem, testid }) => {
  const id = `${testid}-${field.key}`;
  let input;
  if (field.kind === "textarea") input = <textarea rows={4} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  else if (field.kind === "number") input = <input type="number" min={0} max={100} className={inputCls} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} data-testid={id} />;
  else if (field.kind === "color") input = <div className="flex items-center gap-2"><input type="color" value={value || "#C85A32"} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded-lg bg-transparent" data-testid={id} /><span className="font-mono text-xs text-[#C5BDB3]">{value}</span></div>;
  else if (field.kind === "image") input = <ImageField value={value} onChange={onChange} testid={id} />;
  else if (field.kind === "strings") input = <StringsField value={value} onChange={onChange} multiline={field.multiline} testid={id} />;
  else if (field.kind === "list") input = <Nested.List field={field} value={value} onChange={onChange} testid={id} />;
  else if (field.kind === "spotify") input = <SpotifyField item={item} setItem={setItem} testid={id} />;
  else input = <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  return <div><Label>{field.label}</Label>{input}</div>;
};

export const ObjectEditor = ({ fields, value, onChange, testid = "field" }) => (
  <>
    {arr(fields).map((f) => (
      <FieldEditor key={f.key} field={f} value={value?.[f.key]} item={value} setItem={onChange} testid={testid}
        onChange={(v) => onChange({ ...(value || {}), [f.key]: v })} />
    ))}
  </>
);

export { inputCls, Label };

Nested.Obj = ObjectEditor;
Nested.List = ListEditor;
